(() => {
  'use strict';
  if (customElements.get('fs-collection-bundle-dialog')) return;
  const assets = new Map();
  const assetURL = (value) => {
    const url = new URL(value, location.origin);
    if (url.protocol !== 'https:' || (url.origin !== location.origin && url.hostname !== 'cdn.shopify.com')) throw new Error('Invalid bundle asset');
    return url.href;
  };
  const loadAsset = (value, kind, elementName) => {
    if (elementName && customElements.get(elementName)) return Promise.resolve();
    const href = assetURL(value);
    if (assets.has(href)) return assets.get(href);
    const promise = new Promise((resolve, reject) => {
      const selector = kind === 'style' ? 'link[rel="stylesheet"]' : 'script[src]';
      let node = Array.from(document.querySelectorAll(selector)).find((item) => (kind === 'style' ? item.href : item.src) === href);
      if (kind === 'style' && node?.sheet) { resolve(); return; }
      const created = !node;
      if (!node) node = document.createElement(kind === 'style' ? 'link' : 'script');
      let timer;
      const finish = (error) => {
        clearTimeout(timer);
        node.removeEventListener('load', onLoad);
        node.removeEventListener('error', onError);
        if (error) { if (created) node.remove(); reject(error); }
        else resolve();
      };
      const onLoad = () => finish(elementName && !customElements.get(elementName) ? new Error('Bundle component unavailable') : null);
      const onError = () => finish(new Error('Bundle asset unavailable'));
      node.addEventListener('load', onLoad, { once: true });
      node.addEventListener('error', onError, { once: true });
      timer = setTimeout(onError, 20000);
      if (created) {
        if (kind === 'style') { node.rel = 'stylesheet'; node.href = href; }
        else { node.src = href; node.async = true; }
        document.head.append(node);
      }
    }).catch((error) => { assets.delete(href); throw error; });
    assets.set(href, promise);
    return promise;
  };

  class CollectionBundleDialog extends HTMLElement {
    connectedCallback() {
      if (this.connected) return;
      this.dialog = this.querySelector('dialog');
      if (!this.dialog || typeof this.dialog.showModal !== 'function') return;
      this.connected = true;
      this.content = this.querySelector('[data-fs-collection-bundle-content]');
      this.feedback = this.querySelector('[data-fs-collection-bundle-feedback]');
      this.status = this.querySelector('[data-fs-collection-bundle-status]');
      this.retry = this.querySelector('[data-fs-collection-bundle-retry]');
      this.closeButton = this.querySelector('[data-fs-collection-bundle-close]');
      this.guard = this.querySelector('[data-fs-collection-bundle-guard]');
      this.onDocumentClick = (event) => {
        const trigger = event.target.closest('[data-fs-collection-bundle-open]');
        if (!trigger || trigger.getAttribute('aria-controls') !== this.dialog.id || event.defaultPrevented || event.button > 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        event.preventDefault();
        this.open(trigger);
      };
      this.onClick = (event) => {
        if (event.target.closest('dialog') !== this.dialog) return;
        if (event.target.closest('[data-fs-collection-bundle-close]')) this.close();
        if (event.target.closest('[data-fs-collection-bundle-retry]')) this.ensureBuilder();
        const review = event.target.closest('.fs-bundle__mobile-review a');
        if (review && this.builder) {
          event.preventDefault();
          const summary = this.builder.querySelector('.fs-bundle__summary');
          if (summary) { summary.setAttribute('tabindex', '-1'); summary.scrollIntoView({ block: 'start', behavior: 'auto' }); summary.focus({ preventScroll: true }); }
        }
        if (event.target === this.dialog && this.backdropStart) {
          const rect = this.dialog.getBoundingClientRect();
          if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) this.close();
        }
        this.backdropStart = false;
      };
      this.onSubmit = (event) => {
        const blocked = this.cartGuard();
        if (!blocked || !this.builder?.contains(event.target)) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        this.builder.status?.(blocked === 'locked' ? 'locked' : 'checking');
      };
      this.onPointerDown = (event) => { this.backdropStart = event.target === this.dialog; };
      this.onCancel = (event) => { if (event.target === this.dialog) { event.preventDefault(); this.close(); } };
      this.onClose = () => { if (!this.dialog.open) this.restore(); };
      this.onKeyDown = (event) => {
        if (event.key !== 'Tab' || event.target.closest('dialog') !== this.dialog) return;
        const focusable = Array.from(this.dialog.querySelectorAll('button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), summary, [tabindex]:not([tabindex="-1"])')).filter((node) => !node.closest('[hidden]') && node.getClientRects().length && (!node.closest('dialog') || node.closest('dialog') === this.dialog));
        const first = focusable[0] || this.closeButton;
        const last = focusable[focusable.length - 1] || first;
        const current = document.activeElement;
        if (event.shiftKey && (current === first || !this.dialog.contains(current))) { event.preventDefault(); last.focus(); }
        else if (!event.shiftKey && (current === last || !this.dialog.contains(current))) { event.preventDefault(); first.focus(); }
      };
      this.onIntent = (event) => {
        const trigger = event.target.closest('[data-fs-collection-bundle-open]');
        if (!trigger || trigger.getAttribute('aria-controls') !== this.dialog.id || this.builder || this.prepared) return;
        // Only warm the read-only shell on real pointer/keyboard intent, never a cart.
        this.prepareBuilder().catch(() => {});
      };
      document.addEventListener('pointerover', this.onIntent, { passive: true });
      document.addEventListener('focusin', this.onIntent);
      document.addEventListener('click', this.onDocumentClick);
      this.dialog.addEventListener('click', this.onClick);
      this.dialog.addEventListener('pointerdown', this.onPointerDown);
      this.dialog.addEventListener('cancel', this.onCancel);
      this.dialog.addEventListener('close', this.onClose);
      this.dialog.addEventListener('keydown', this.onKeyDown);
      this.dialog.addEventListener('submit', this.onSubmit, true);
    }

    disconnectedCallback() {
      if (!this.connected) return;
      this.connected = false;
      this.controller?.abort();
      this.catalogController?.abort();
      this.guardObserver?.disconnect();
      this.close(true);
      this.restore();
      document.removeEventListener('pointerover', this.onIntent);
      document.removeEventListener('focusin', this.onIntent);
      this.prepared = null;
      document.removeEventListener('click', this.onDocumentClick);
      this.dialog.removeEventListener('click', this.onClick);
      this.dialog.removeEventListener('pointerdown', this.onPointerDown);
      this.dialog.removeEventListener('cancel', this.onCancel);
      this.dialog.removeEventListener('close', this.onClose);
      this.dialog.removeEventListener('keydown', this.onKeyDown);
      this.dialog.removeEventListener('submit', this.onSubmit, true);
    }

    sectionURL() {
      const url = new URL(this.dataset.sectionUrl, location.origin);
      if (url.origin !== location.origin || url.searchParams.get('section_id') !== 'fs-bundle') throw new Error('Invalid bundle section');
      const preview = this.dataset.previewThemeId;
      if (preview && /^\d+$/.test(preview)) url.searchParams.set('preview_theme_id', preview);
      return url;
    }

    lockScroll() {
      if (this.scrollLock) return;
      const body = document.body;
      const html = document.documentElement;
      const properties = ['overflow', 'position', 'top', 'left', 'width'];
      this.scrollLock = { x: window.scrollX, y: window.scrollY, body: properties.map((name) => [name, body.style[name]]), overflow: html.style.overflow, behavior: html.style.scrollBehavior };
      html.style.overflow = 'hidden';
      body.style.overflow = 'hidden';
      body.style.position = 'fixed';
      body.style.top = -this.scrollLock.y + 'px';
      body.style.left = -this.scrollLock.x + 'px';
      body.style.width = '100%';
    }

    restore() {
      this.builder?.querySelectorAll('fs-bundle-product').forEach((product) => { product.close?.(); product.unlockScroll?.(); });
      const saved = this.scrollLock;
      if (!saved) return;
      this.scrollLock = null;
      saved.body.forEach(([name, value]) => { document.body.style[name] = value; });
      document.documentElement.style.overflow = saved.overflow;
      document.documentElement.style.scrollBehavior = 'auto';
      window.scrollTo({ left: saved.x, top: saved.y, behavior: 'instant' });
      document.documentElement.style.scrollBehavior = saved.behavior;
      const opener = this.opener?.isConnected ? this.opener : Array.from(document.querySelectorAll('[data-fs-collection-bundle-open]')).find((node) => node.getAttribute('aria-controls') === this.dialog.id && node.getClientRects().length);
      opener?.focus({ preventScroll: true });
    }

    cartGuard() {
      const root = document.documentElement;
      if (root.hasAttribute('data-fs-quickview-cart-uncertain') || root.hasAttribute('data-fs-collection-cart-uncertain') || root.hasAttribute('data-fs-selection-cart-uncertain') || Array.from(document.querySelectorAll('[data-fs-hoodie-grid]')).some((node) => node.fhg?.blocked)) return 'locked';
      try { if (sessionStorage.getItem('fs-bundle-pending-add')) return 'locked'; } catch (_) {}
      if (root.hasAttribute('data-fs-quickview-cart-pending') || root.hasAttribute('data-fs-collection-cart-pending') || root.hasAttribute('data-fs-selection-cart-pending') || root.hasAttribute('data-fs-live-cart-checkout-locked') || document.querySelector('.cart__items--disabled') || Array.from(document.querySelectorAll('[data-fs-hoodie-grid]')).some((node) => node.fhg?.pending)) return 'pending';
      return '';
    }

    open(trigger) {
      if (this.dialog.open) return;
      const blocked = this.cartGuard();
      if (blocked) {
        this.guard.textContent = blocked === 'locked' ? this.dataset.lockedLabel : this.dataset.checkingLabel;
        this.guard.hidden = false;
        this.guardObserver?.disconnect();
        this.guardObserver = new MutationObserver(() => {
          if (!this.cartGuard()) { this.guard.hidden = true; this.guardObserver.disconnect(); }
        });
        this.guardObserver.observe(document.documentElement, { attributes: true });
        return;
      }
      this.guard.hidden = true;
      this.guardObserver?.disconnect();
      this.opener = trigger;
      this.lockScroll();
      try { this.dialog.showModal(); } catch (_) { this.restore(); return; }
      this.closeButton.focus({ preventScroll: true });
      if (this.builder) {
        if (!this.builder.busy) this.builder.load({ reuseCatalog: true });
      } else this.ensureBuilder();
    }

    close(force = false) {
      if (!force && this.builder?.ready && this.builder.busy) { this.builder.status?.('checking'); return; }
      this.builder?.querySelectorAll('fs-bundle-product').forEach((product) => { product.close?.(); product.unlockScroll?.(); });
      if (this.dialog.open) this.dialog.close();
    }

    ensureBuilder() {
      if (this.builder) return Promise.resolve(this.builder);
      if (this.pending) return this.pending;
      this.feedback.hidden = false;
      this.status.textContent = this.dataset.loadingLabel;
      this.retry.hidden = true;
      this.content.setAttribute('aria-busy', 'true');
      // Fetch catalog page 1 alongside the shell, rather than after it mounts.
      // This runs only on an explicit open, not on hover/focus warmup.
      const firstPage = Promise.resolve().then(() => this.firstCatalogPage()).catch((error) => ({ error }));
      this.pending = Promise.resolve().then(() => this.prepareBuilder()).then((builder) => {
        if (!this.isConnected) return null;
        builder.initialCatalogPage = firstPage;
        this.content.replaceChildren(builder);
        this.builder = builder;
        this.feedback.hidden = true;
        return builder;
      }).catch((error) => {
        if (this.isConnected) {
          this.status.textContent = this.dataset.errorLabel;
          this.retry.hidden = false;
        }
        return null;
      }).finally(() => { this.pending = null; this.content.setAttribute('aria-busy', 'false'); });
      return this.pending;
    }

    firstCatalogPage() {
      let url;
      try {
        url = new URL(this.dataset.catalogUrl, location.origin);
        if (url.origin !== location.origin || url.searchParams.get('view') !== 'bundle-data' || !/\/collections\//.test(url.pathname)) throw new Error('Invalid bundle catalog');
        // The opened preview page already established its theme cookie. Match the
        // builder's proven AJAX feed request rather than invoking preview redirect.
        url.searchParams.set('page', '1');
        url.searchParams.set('_fs', String(Date.now()));
      } catch (error) { return Promise.resolve({ error }); }
      const controller = new AbortController();
      this.catalogController = controller;
      let timer;
      const deadline = new Promise((_, reject) => { timer = setTimeout(() => { reject(new Error('Bundle catalog timed out')); controller.abort(); }, 12000); });
      const operation = Promise.resolve().then(() => fetch(url.href, { credentials: 'same-origin', cache: 'no-store', signal: controller.signal }))
        .then((response) => { if (!response.ok) throw new Error('Bundle catalog unavailable'); return response.json(); });
      return Promise.race([operation, deadline]).then((data) => ({ data }), (error) => ({ error }))
        .finally(() => { clearTimeout(timer); controller.abort(); if (this.catalogController === controller) this.catalogController = null; });
    }

    prepareBuilder() {
      if (this.prepared) return this.prepared;
      const pending = this.loadBuilder().catch((error) => {
        if (this.prepared === pending) this.prepared = null;
        throw error;
      });
      this.prepared = pending;
      return pending;
    }

    async loadBuilder() {
      const controller = new AbortController();
      this.controller = controller;
      let timer;
      const deadline = new Promise((_, reject) => { timer = setTimeout(() => { reject(new Error('Bundle controls timed out')); controller.abort(); }, 14000); });
      try {
        const operation = Promise.resolve().then(() => Promise.all([
          fetch(this.sectionURL().href, { method: 'GET', credentials: 'same-origin', cache: 'no-store', signal: controller.signal, headers: { Accept: 'text/html' } }).then((response) => { if (!response.ok) throw new Error('Bundle section unavailable'); return response.text(); }),
          loadAsset(this.dataset.builderStyle, 'style'),
          loadAsset(this.dataset.productStyle, 'style'),
          loadAsset(this.dataset.productScript, 'script', 'fs-bundle-product'),
          loadAsset(this.dataset.builderScript, 'script', 'fs-bundle-builder')
        ]));
        const [html] = await Promise.race([operation, deadline]);
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const builders = parsed.querySelectorAll('fs-bundle-builder[data-fs-bundle]');
        if (builders.length !== 1 || !builders[0].querySelector('[data-fs-bundle-config]')) throw new Error('Bundle section missing');
        const builder = builders[0];
        builder.querySelectorAll('script').forEach((script) => { if (script.type !== 'application/json') script.remove(); });
        return document.importNode(builder, true);
      } catch (error) {
        controller.abort();
        throw error;
      } finally { clearTimeout(timer); controller.abort(); if (this.controller === controller) this.controller = null; }
    }
  }
  customElements.define('fs-collection-bundle-dialog', CollectionBundleDialog);
})();
